<template>
  <el-dialog
    :title="text.mappingTitle"
    :visible="visible"
    width="560px"
    @update:visible="$emit('update:visible', $event)">
    <el-form label-width="90px" size="small">
      <el-form-item :label="text.storeLabel">
        <span>{{ storeLabel }}</span>
      </el-form-item>
      <el-form-item :label="text.mappingCurrentLabel">
        <span>{{ currentText }}</span>
      </el-form-item>
      <el-form-item :label="text.mappingDestLabel">
        <TransferDestinationPicker
          v-model="destination"
          :operators="operators"
          :current="currentMapping"
          :reset-seq="resetSeq" />
      </el-form-item>
      <el-form-item :label="text.mappingReasonLabel">
        <el-input v-model="reason" maxlength="200" clearable />
      </el-form-item>
      <el-form-item :label="text.mappingMemoLabel">
        <el-input v-model="memo" type="textarea" :rows="2" maxlength="200" />
      </el-form-item>
    </el-form>

    <p v-if="serverMessage" class="store-mapping__error">{{ serverMessage }}</p>

    <span slot="footer">
      <el-button size="small" @click="$emit('update:visible', false)">{{ text.cancelAction }}</el-button>
      <el-button size="small" type="primary" :loading="saving" :disabled="!destination" @click="save">{{
        text.mappingSaveAction
      }}</el-button>
    </span>
  </el-dialog>
</template>

<script>
import TransferDestinationPicker from '@/components/callpop/TransferDestinationPicker.vue'
import { httpErrorMessage } from '@/plugins/http/policy'
import {
  CALLPOP_FORM_WRITE_CONFIG,
  autoJoinText,
  autoJoinToastType,
  mappingBody,
  storeMappingUrl,
  storeOwnerView,
} from '@/utils/callpopOrg'
import { CALLPOP_TEXT } from '@/utils/callpopText'

/**
 * 매장 → 운영사·관리점 매핑(등록·이관) — `PUT /callpop/stores/{storeCd}/mapping`.
 *
 * 저장 응답에는 매핑과 함께 **자동 참여의 결과**(`autoJoin` 5갈래)가 실린다(§3-5). 그 사실을 토스트로
 * 말하지 않으면 「매핑했는데 참여가 안 됐다」를 사람이 알 길이 없고, 특히 `FAILED` 는 **매핑은 남는다**는
 * 사실을 함께 말해야 사람이 매핑을 다시 누르지 않는다 — 문장은 `callpopOrg` 한 곳이 소유한다.
 *
 * 이관 자격(§3-6)은 서버가 판정한다. 403 은 세션을 끝내지 않고([CALLPOP_FORM_WRITE_CONFIG]) 그 문장이 폼에 앉는다.
 */
export default {
  name: 'StoreMappingDialog',

  components: { TransferDestinationPicker },

  props: {
    visible: { type: Boolean, default: false },
    /** 매장 입점 현황의 행 */
    store: { type: Object, default: null },
    operators: { type: Array, default: () => [] },
  },

  data() {
    return {
      text: CALLPOP_TEXT,
      destination: null,
      reason: '',
      memo: '',
      serverMessage: '',
      saving: false,
      /** 다이얼로그를 다시 열 때마다 올린다 — 목적지 선택기가 처음 상태로 돌아간다 */
      resetSeq: 0,
    }
  },

  computed: {
    storeLabel() {
      const row = this.store || {}

      return [row.storeNm, row.storeId].filter(Boolean).join(' · ')
    },
    currentMapping() {
      const row = this.store || {}

      return { operator: row.operator || null, agency: row.agency || null }
    },
    currentText() {
      const owner = storeOwnerView(this.store)

      return [owner.operatorText, owner.agencyText].filter(Boolean).join(' · ')
    },
  },

  watch: {
    visible(open) {
      if (!open) return
      this.destination = null
      this.reason = ''
      this.memo = ''
      this.serverMessage = ''
      this.resetSeq += 1
    },
  },

  methods: {
    async save() {
      if (!this.store || !this.destination) return

      this.serverMessage = ''
      this.saving = true

      try {
        const response = await this.$axios.put(
          storeMappingUrl(this.store.storeCd),
          mappingBody(this.destination, this.reason, this.memo),
          CALLPOP_FORM_WRITE_CONFIG
        )
        const autoJoin = response.data && response.data.autoJoin
        const note = autoJoinText(autoJoin)

        if (note) this.$message({ type: autoJoinToastType(autoJoin), message: note })
        this.$emit('saved', response.data)
        this.$emit('update:visible', false)
      } catch (error) {
        this.serverMessage = httpErrorMessage(error, this.text.mappingDenied)
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.store-mapping__error {
  color: #f56c6c;
  font-size: 12px;
  margin: 0 0 4px;
}
</style>
